import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { GlassButton } from 'dashboard/components/common';
import { libraryLabel } from 'utilities/libraryLabels';

/**
 * The page one entry of this type opens as.
 *
 * Writing a blog post and deciding what a blog post *page* looks like are the
 * same job to the person doing it, and they were in different parts of the
 * dashboard with nothing linking them. The layout lived in the page builder,
 * on a page you had to know to create and then mark as the detail page for
 * this type — three steps, none of them mentioned here, and no sign on this
 * screen that the capability existed at all.
 *
 * So: this says whether there is one, and takes you to it. It deliberately
 * does not reproduce the editor. There is one place to design a page and it
 * is the page builder; a second set of controls here would be a second thing
 * to keep in step, and the first to drift.
 */
export default function DetailPageCard({ stage, brandId }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [checked, setChecked] = useState(false);
  const [creating, setCreating] = useState(false);

  const look = useCallback(() => {
    if (!brandId) return;
    API.listSitePages({ brand_id: brandId })
      .then((rows) => {
        const match = (Array.isArray(rows) ? rows : []).find((p) => p.detailOf === stage);
        setPage(match || null);
      })
      // A failed lookup leaves the card quiet rather than claiming there is
      // no detail page — offering to create a second one would be worse than
      // saying nothing.
      .catch(() => setPage(null))
      .finally(() => setChecked(true));
  }, [brandId, stage]);

  useEffect(() => { look(); }, [look]);

  const create = () => {
    setCreating(true);
    const label = libraryLabel(stage);
    API.createSitePage({
      brand_id: brandId,
      title: `${label.replace(/s$/, '')} page`,
      // The slug the public routes already expect for an entry of this type,
      // so the page works the moment it exists rather than after somebody
      // finds the right box in settings.
      slug: `${stage}-detail`,
      detailOf: stage,
      status: 'draft',
    })
      .then((created) => {
        toast.success('Made you a page to design. It is a draft until you publish it.');
        navigate(`/dashboard/webpage/pages?page=${created?._id || ''}`);
      })
      .catch((err) => toast.error(err?.message || 'Could not create that page'))
      .finally(() => setCreating(false));
  };

  if (!checked) return null;

  return (
    <Card sx={{ mb: '20px' }}>
      <CardContent sx={{ p: '24px', '&:last-child': { pb: '24px' } }}>
        <Stack
          direction='row'
          alignItems='flex-start'
          justifyContent='space-between'
          gap='16px'
          flexWrap='wrap'
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction='row' alignItems='center' spacing='8px' mb='4px'>
              <ArticleIcon fontSize='small' color='primary' />
              <Typography variant='h6' fontWeight='bold'>
                How one of these looks when opened
              </Typography>
            </Stack>
            <Typography variant='body2' color='text.secondary'>
              {page
                ? 'Every entry of this type is shown through this page. Change it once and '
                  + 'they all change.'
                : 'There is no page for these yet, so clicking one on your site goes nowhere. '
                  + 'One page designs all of them.'}
            </Typography>
          </Box>

          {page ? (
            <GlassButton onClick={() => navigate(`/dashboard/webpage/pages?page=${page._id}`)}>
              Design it
            </GlassButton>
          ) : (
            <GlassButton
              variant='contained'
              onClick={create}
              disabled={creating || !brandId}
              disabledReason={creating ? 'Creating…' : 'Choose a brand first.'}
            >
              {creating ? 'Creating…' : 'Make one'}
            </GlassButton>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
